import { useState, useEffect } from 'react';
import { Card, LoadingSpinner } from '../components/Layout';
import { format, isValid } from 'date-fns';
import api from '../services/api';

const safeFormatDate = (dateStr: string | undefined | null, formatStr: string, fallback = 'N/A'): string => {
  if (!dateStr) return fallback;
  try {
    // Calendar-date values ("2026-04-16" or "2026-04-16T00:00:00.000Z") are days, not moments.
    // Anchor at noon local to avoid UTC-boundary shift showing Apr 16 as Apr 15 in CT.
    const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
    const d = match ? new Date(`${match[1]}T12:00:00`) : new Date(dateStr);
    if (!isValid(d)) return fallback;
    return format(d, formatStr);
  } catch {
    return fallback;
  }
};

interface Punch {
  id: string;
  punch_type: 'in' | 'out';
  punch_time: string;
}

interface DayRecord {
  date: string;
  firstIn: string | null;
  lastOut: string | null;
  hours: number;
}

export function TimeClockPage() {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<DayRecord[]>([]);
  const [todayPunches, setTodayPunches] = useState<Punch[]>([]);
  const [weeklyHours, setWeeklyHours] = useState(0);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState<string | null>(null);
  const [now, setNow] = useState(new Date());
  // Punch-correction request (forgot/missed punch → HR review)
  const [corrections, setCorrections] = useState<any[]>([]);
  const [showCorr, setShowCorr] = useState(false);
  const [corrForm, setCorrForm] = useState({ punchTime: '', punchType: 'in' as 'in' | 'out', reason: '' });
  const [corrMsg, setCorrMsg] = useState('');
  const [submittingCorr, setSubmittingCorr] = useState(false);

  useEffect(() => {
    load();
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try {
      const [todayAssignment, hours, hist, punches] = await Promise.all([
        api.getTodaysAssignment().catch(() => null),
        api.getWeeklyHours(weekStart()).catch(() => ({ hours: 0 })),
        api.getMyTimeHistory(14).catch(() => []),
        api.getTodaysPunches().catch(() => []),
      ]);
      setIsClockedIn(!!todayAssignment?.clockedIn);
      setClockInTime(todayAssignment?.clock_in_time || null);
      setWeeklyHours(hours?.hours ?? 0);
      setHistory(hist || []);
      setTodayPunches(punches || []);
      api.get('/employee-shifts/my-punch-corrections').then((r) => setCorrections(r.data || [])).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  const submitCorrection = async (e: React.FormEvent) => {
    e.preventDefault();
    setCorrMsg('');
    if (!corrForm.punchTime) { setCorrMsg('Pick the date & time you meant to punch'); return; }
    setSubmittingCorr(true);
    try {
      await api.post('/employee-shifts/punch-correction', {
        punch_time: new Date(corrForm.punchTime).toISOString(),
        punch_type: corrForm.punchType,
        reason: corrForm.reason,
      });
      setCorrMsg('✓ Sent to HR for review');
      setCorrForm({ punchTime: '', punchType: 'in', reason: '' });
      const r = await api.get('/employee-shifts/my-punch-corrections');
      setCorrections(r.data || []);
      setTimeout(() => { setCorrMsg(''); setShowCorr(false); }, 2500);
    } catch { setCorrMsg('Could not submit — try again'); }
    finally { setSubmittingCorr(false); }
  };

  // Pay period = Thursday → Wednesday. Back up to the most recent Thursday (today if Thu).
  const weekStart = () => {
    const d = new Date();
    const dow = d.getDay(); // 0=Sun..6=Sat
    const daysBackToThursday = (dow - 4 + 7) % 7; // Thu=4
    d.setDate(d.getDate() - daysBackToThursday);
    return format(d, 'yyyy-MM-dd');
  };

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const formatShort = (s: string) => {
    const d = new Date(s);
    return isValid(d) ? format(d, 'h:mm a') : '--';
  };

  // Pair today's punches into sessions: [{in, out, duration}, ...]
  const todaySessions: Array<{ inTime: string; outTime: string | null; minutes: number }> = [];
  let todayWorkedMinutes = 0;
  let todayBreakMinutes = 0;
  for (let i = 0; i < todayPunches.length; i++) {
    const p = todayPunches[i];
    if (p.punch_type === 'in') {
      const outPunch = todayPunches[i + 1]?.punch_type === 'out' ? todayPunches[i + 1] : null;
      const inDate = new Date(p.punch_time);
      const outDate = outPunch ? new Date(outPunch.punch_time) : null;
      const mins = outDate ? (outDate.getTime() - inDate.getTime()) / 60000 : (new Date().getTime() - inDate.getTime()) / 60000;
      todaySessions.push({ inTime: p.punch_time, outTime: outPunch?.punch_time || null, minutes: mins });
      todayWorkedMinutes += mins;
      if (outPunch) i++; // skip the paired out
    }
  }
  // Calculate breaks (gaps between sessions)
  for (let i = 1; i < todaySessions.length; i++) {
    const prevEnd = todaySessions[i - 1].outTime;
    const nextStart = todaySessions[i].inTime;
    if (prevEnd) {
      todayBreakMinutes += (new Date(nextStart).getTime() - new Date(prevEnd).getTime()) / 60000;
    }
  }

  const formatMins = (m: number) => {
    const totalMin = Math.round(m);
    const h = Math.floor(totalMin / 60);
    const min = totalMin % 60;
    return h > 0 ? `${h}h ${min}m` : `${min}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">My Time</h1>
        <p className="text-gray-400 text-sm mt-1">
          Punches come from the CrossChex fingerprint clock. See a supervisor if anything is wrong.
        </p>
      </div>

      <Card className="text-center py-8 border-2 border-yellow-400/50">
        <div className="text-gray-400 text-sm mb-2">{format(now, 'EEEE, MMMM d, yyyy')}</div>
        <div className="text-5xl font-mono font-bold text-yellow-400 mb-6">{formatTime(now)}</div>
        {isClockedIn ? (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-full">
            <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            Clocked In{clockInTime && ` since ${formatShort(clockInTime)}`}
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-gray-400 rounded-full">
            <span className="w-3 h-3 bg-gray-500 rounded-full" />
            Not Clocked In
          </div>
        )}
      </Card>

      {/* Today's Activity */}
      {todaySessions.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-yellow-400">Today</h3>
            <div className="flex gap-4 text-sm">
              <span className="text-green-400">{formatMins(todayWorkedMinutes)} worked</span>
              {todayBreakMinutes > 0 && (
                <span className="text-orange-400">{formatMins(todayBreakMinutes)} break</span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {todaySessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${s.outTime ? 'bg-gray-500' : 'bg-green-400 animate-pulse'}`} />
                  <div>
                    <span className="text-white font-medium">{formatShort(s.inTime)}</span>
                    <span className="text-gray-500 mx-2">→</span>
                    <span className={s.outTime ? 'text-white font-medium' : 'text-green-400'}>
                      {s.outTime ? formatShort(s.outTime) : 'Now'}
                    </span>
                  </div>
                </div>
                <span className="text-gray-400 text-sm">{formatMins(s.minutes)}</span>
              </div>
            ))}
            {/* Show breaks between sessions */}
            {todaySessions.length > 1 && (
              <div className="pt-2 border-t border-gray-700 mt-2">
                <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">Breaks</div>
                {todaySessions.slice(1).map((s, i) => {
                  const prevOut = todaySessions[i].outTime;
                  if (!prevOut) return null;
                  const breakMins = (new Date(s.inTime).getTime() - new Date(prevOut).getTime()) / 60000;
                  return (
                    <div key={`break-${i}`} className="flex items-center justify-between text-sm px-3 py-1">
                      <span className="text-gray-500">
                        {formatShort(prevOut)} → {formatShort(s.inTime)}
                      </span>
                      <span className="text-orange-400">{formatMins(breakMins)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <h3 className="text-lg font-semibold text-yellow-400 mb-1">This Pay Period</h3>
        <div className="text-gray-500 text-xs mb-4">Thursday – Wednesday</div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-400">Hours Worked</span>
          <span className="text-2xl font-bold text-white">{weeklyHours.toFixed(1)} hrs</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-4">
          <div
            className="bg-yellow-400 h-4 rounded-full transition-all"
            style={{ width: `${Math.min((weeklyHours / 40) * 100, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-gray-500">0 hrs</span>
          <span className="text-gray-500">40 hrs target</span>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-yellow-400 mb-4">Recent Days</h3>
        {history.length > 0 ? (
          <div className="space-y-2">
            {history.map((r) => (
              <div key={r.date} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                <div>
                  <div className="text-white font-medium">{safeFormatDate(r.date, 'EEE, MMM d')}</div>
                  <div className="text-gray-400 text-sm">
                    {r.firstIn ? formatShort(r.firstIn) : '--'} – {r.lastOut ? formatShort(r.lastOut) : 'In Progress'}
                  </div>
                </div>
                <div className="text-green-400 font-medium text-lg">{r.hours.toFixed(1)} hrs</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No clock history yet</p>
        )}
      </Card>

      {/* Forgot / missed punch → request a correction (goes to HR, not automatic) */}
      <Card>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-yellow-400">Forgot a Punch?</h3>
          <button onClick={() => setShowCorr(!showCorr)} className="text-sm text-yellow-400 hover:underline">
            {showCorr ? 'Cancel' : 'Request a correction'}
          </button>
        </div>
        <p className="text-gray-500 text-xs mt-1">
          Missed clocking in or out? Send the correct time to HR. It won't change your clock until they review and approve it.
        </p>
        {showCorr && (
          <form onSubmit={submitCorrection} className="mt-3 space-y-3">
            {corrMsg && <div className={`text-sm ${corrMsg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{corrMsg}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Date &amp; time</label>
                <input type="datetime-local" value={corrForm.punchTime} onChange={(e) => setCorrForm({ ...corrForm, punchTime: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-[#111] border border-[#333] text-white focus:border-[#FFCD24] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Punch type</label>
                <select value={corrForm.punchType} onChange={(e) => setCorrForm({ ...corrForm, punchType: e.target.value as 'in' | 'out' })}
                  className="w-full px-3 py-2 rounded-lg bg-[#111] border border-[#333] text-white focus:border-[#FFCD24] focus:outline-none">
                  <option value="in">Clock IN</option>
                  <option value="out">Clock OUT</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Reason</label>
                <input value={corrForm.reason} onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })} placeholder="e.g. forgot to clock out"
                  className="w-full px-3 py-2 rounded-lg bg-[#111] border border-[#333] text-white focus:border-[#FFCD24] focus:outline-none" />
              </div>
            </div>
            <button type="submit" disabled={submittingCorr}
              className="bg-[#FFCD24] hover:bg-yellow-400 text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
              {submittingCorr ? 'Sending…' : 'Send to HR'}
            </button>
          </form>
        )}
        {corrections.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-700 space-y-2">
            <p className="text-xs text-gray-500 uppercase">Your recent requests</p>
            {corrections.slice(0, 5).map((c: any) => (
              <div key={c.id} className="flex items-center justify-between text-sm gap-2">
                <span className="text-gray-300">{new Date(c.requested_punch_time).toLocaleString()} · {String(c.punch_type).toUpperCase()}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${c.status === 'approved' ? 'bg-green-500/20 text-green-400' : c.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
